import Vue from 'vue';
import axios from 'axios';
import BootstrapVue from 'bootstrap-vue';
import { getModule } from 'vuex-module-decorators';
import {
  ValidationProvider, ValidationObserver, localize, extend,
} from 'vee-validate';
import * as rules from 'vee-validate/dist/rules';
import VeeLocaleZhTW from 'vee-validate/dist/locale/zh_TW.json';
import Vuetable from 'vuetable-2';
import VueSwitches from 'vue-switches';
import vSelect from 'vue-select';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import CKEditor from '@ckeditor/ckeditor5-vue';

import VueAxios from '@/plugins/axios';
import VueCookie from '@/plugins/cookie';

import './install-theme';

import { ignoreCancel } from './configs/app';
import App from './App.vue';
import router from './router';
import store from './store';
import AppModule from './store/modules/app';
import UserModule from './store/modules/user';
// import LoadingModule from './store/modules/loading';

/* Piaf component */
import Notifications from './components/piaf/Common/Notification';
import Colxx from './components/piaf/Common/Colxx.vue';
import Breadcrumb from './components/piaf/Common/Breadcrumb.vue';

/* App tools */
import fullWidthMax from './libs/vee-validate/rules/full-width-max';
import imageHeight from './libs/vee-validate/rules/image-height';
import imageWidth from './libs/vee-validate/rules/image-width';
import imageMaxHeight from './libs/vee-validate/rules/image-max-height';
import imageMaxWidth from './libs/vee-validate/rules/image-max-width';
import imageMinHeight from './libs/vee-validate/rules/image-min-height';
import imageMinWidth from './libs/vee-validate/rules/image-min-width';
import getCookieName from './utils/cookie';

Vue.use(BootstrapVue);
Vue.use(VueAxios);
Vue.use(VueCookie);
Vue.use(Notifications);
Vue.use(CKEditor);

/* Piaf component */
Vue.component('b-colxx', Colxx);
Vue.component('piaf-breadcrumb', Breadcrumb);
Vue.component('vuetable', Vuetable);
Vue.component('switches', VueSwitches);
Vue.component('vue-perfect-scrollbar', VuePerfectScrollbar);
Vue.component('v-select', vSelect);

// Register it globally
// eslint-disable-next-line no-restricted-syntax
for (const [rule, validation] of Object.entries(rules)) {
  extend(rule, {
    ...validation,
  });
}
extend(fullWidthMax.name, {
  ...fullWidthMax,
});
extend(imageHeight.name, {
  ...imageHeight,
});
extend(imageWidth.name, {
  ...imageWidth,
});
extend(imageMaxHeight.name, {
  ...imageMaxHeight,
});
extend(imageMaxWidth.name, {
  ...imageMaxWidth,
});
extend(imageMinHeight.name, {
  ...imageMinHeight,
});
extend(imageMinWidth.name, {
  ...imageMinWidth,
});
localize('zh_TW', VeeLocaleZhTW);
Vue.component('validation-observer', ValidationObserver);
Vue.component('validation-provider', ValidationProvider);

Vue.config.productionTip = false;

router.beforeEach((to, _from, next) => {
  // console.log('before each');
  if (store.getters['app/hasCancelTokens']) {
    store.dispatch('app/cancelAllRequest', 'ABORT');
  }

  const requireAuth = to.matched.some((record) => record.meta.auth);
  const requireRoles = to.matched.some((record) => record.meta.roles);

  // console.log(store.getters['user/isLogin']);
  // console.log(to.meta);
  // console.log(`requireAuth: ${requireAuth}`);
  // console.log(`requireRoles: ${requireRoles}`);

  // need login
  if (requireAuth) {
    // if no login
    if (!store.getters['user/isLogin']) {
      const appModule = getModule(AppModule, store);
      appModule.setSessionMessage({
        type: 'danger',
        message: '請先登入。',
      });
      next({
        name: 'auth-login',
        query: {
          redirect: to.fullPath,
        },
      });
      return;
    }

    // need sepcial role
    if (requireRoles) {
      const adminRole = store.getters['user/getRoleId'] as number;
      const allowRoles = (to.meta.roles as number[]).includes(adminRole);
      // console.log(allowRoles);
      if (allowRoles === false) {
        console.warn('權限不足');
        next({
          name: 'error-403',
        });
        return;
      }
    }
  }

  next();
});

new Vue({
  router,
  store,
  render: (h) => h(App),
  // beforeCreate() {
  //   const adminToken = this.$cookie.get(getCookieName('token'));
  //   const adminData = this.$cookie.get(getCookieName('admin'))
  //     ? JSON.parse(this.$cookie.get(getCookieName('admin')) as string) : null;
  //   store.commit('user/setUser', {
  //     token: adminToken,
  //     ...adminData,
  //   });
  // },
  beforeMount() {
    /* Set Axios request */
    this.$axios.interceptors.request.use((config) => {
      const newConfig = config;
      // console.log(config);

      if (!ignoreCancel.includes(config.url as string)) {
        const cancelSource = axios.CancelToken.source();
        const appModule = getModule(AppModule, this.$store);
        newConfig.cancelToken = cancelSource.token;
        // this.$store.commit('app/addCancelToken', cancelSource);
        appModule.addCancelToken({
          source: cancelSource,
          path: config.url as string,
        });
      }

      if (this.$store.getters['user/isLogin']) {
        newConfig.headers.common.Authorization = this.$store.getters['user/getToken'];
      }

      return newConfig;
    });

    this.$axios.interceptors.response.use((response) => {
      // console.log(response);
      if (response.config.cancelToken) {
        const appModule = getModule(AppModule, this.$store);
        appModule.deleteCancelToken(response.config.cancelToken);
      }

      return response;
    }, (error) => {
      if (error.response.status) {
        if (error.response.status === 403) {
          this.$notify.show('error filled', '登入階段已過期，請重新登入', '金旺宏形象網站管理後台');
          const appModule = getModule(AppModule, this.$store);
          const userModule = getModule(UserModule, this.$store);
          appModule.cancelAllRequest(`[${error.response.status}] ${error.response.statusText}`);

          // Clean cookies and redirect to login
          userModule.clearUser();
          this.$cookie.remove(getCookieName('token'));
          this.$cookie.remove(getCookieName('admin'));
          appModule.setSessionMessage({
            type: 'danger',
            message: '請先登入。',
          });
          router.replace({
            name: 'auth-login',
            query: {
              redirect: this.$route.fullPath,
            },
          });
        }
      }

      return Promise.reject(error.response.data);
    });

    /* Find logedin user */
    // const adminToken = this.$cookie.get(getCookieName('token'));
    // const adminData = this.$cookie.get(getCookieName('admin'))
    // ? JSON.parse(this.$cookie.get(getCookieName('admin')) as string) : null;

    // if (adminToken && adminData) {
    //   const userModule = getModule(UserModule, this.$store);
    //   userModule.setUser({
    //     ...adminData,
    //     token: adminToken,
    //   });
    // }

    /* Prefetch data */
    this.$store.dispatch('app/fetchMenuProjects', this);
  },
}).$mount('#app');
